import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAICredits,
  getAvailableModels,
  getWorkspaceAISettings,
  updateWorkspaceAISettings,
} from "./modelEndpoints";
import type {
  AICreditsResponse,
  AIModelsResponse,
  ModelSurface,
  WorkspaceAISettings,
} from "./aiModels.types";

export const useAIModels = ({
  workspaceId,
  surface = "agent",
  enabled = true,
}: {
  workspaceId?: string;
  surface?: ModelSurface;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ["aiModels", workspaceId, surface],
    queryFn: () => getAvailableModels({ workspace_id: workspaceId as string, surface }),
    select: (res): AIModelsResponse => res.data,
    enabled: Boolean(workspaceId) && enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useWorkspaceAISettings = ({
  workspaceId,
  enabled = true,
}: {
  workspaceId?: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ["aiSettings", workspaceId],
    queryFn: () => getWorkspaceAISettings({ workspace_id: workspaceId as string }),
    select: (res): WorkspaceAISettings => res.data,
    enabled: Boolean(workspaceId) && enabled,
  });

export const useAICredits = ({
  workspaceId,
  enabled = true,
}: {
  workspaceId?: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ["aiCredits", workspaceId],
    queryFn: () => getAICredits({ workspace_id: workspaceId as string }),
    select: (res): AICreditsResponse => res.data,
    enabled: Boolean(workspaceId) && enabled,
  });

export const useUpdateWorkspaceAISettings = (workspaceId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<WorkspaceAISettings>) =>
      updateWorkspaceAISettings({ workspace_id: workspaceId as string, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["aiSettings", workspaceId] });
    },
  });
};
